/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: data.controls.ts
 * Project: mle-client-n
 * Committed last: 2022/1/28 @ 1658
 * ---
 * Description:
 */

import {Editor} from '../../../../store/observables/Editor';
import {ControlGroup, controlGroupBuilder, ControlGroupLayouts,} from '../../helpers/ControlGroup';
import {ControlTypes} from '../../../selection-properties';
import {ObservableBarcode} from '../../../../store/observables/ObservableBarcode';
import {ShapeTypes} from '../../../enums';
import {SimpleTextShapeControl} from '../../helpers/SimpleTextShapeControl';
import {SelectSCItem, SelectShapeControl} from '../../helpers/SelectShapeControl';
import {BooleanShapeControl} from '../../helpers/BooleanShapeControl';
import {ObservableText} from '../../../../store/observables/ObservableText';
import {ObservableVariable} from '../../../../store/observables/ObservableVariable';
import {TextAreaShapeControl} from '../../helpers/TextAreaShapeControl';
import {linkFieldPopoverRenderer} from "../../../../components/common/overlay/LinkFieldPopoverRenderer";

export enum DataControlGroupTypes {
    CODE = 'Codice',
    ENCODING = 'Specifica',
    ADDITIONS = 'Controlli aggiuntivi',
    TEXT_CONTENT = 'Contenuto',
    VAR_NAME = 'Nome variabile'
}

export const barcodeEncodings: SelectSCItem<string>[] = [
    {
        value: 'CODE39',
    },
    {
        value: 'CODE128',
    },
    {
        value: 'EAN13'
    },
    {
        value: 'EAN8',
    },
    {
        value: 'EAN5',
    },
    {
        value: 'EAN2'
    },
    {
        value: 'ITF'
    },
    {
        value: 'ITF14',
    },
    {
        value: 'MSI10'
    },
    {
        value: 'MSI11'
    },
    {
        value: 'MSI1010'
    },
    {
        value: 'MSI1110'
    }
];

const codeControlGroup = (editor: Editor) => new ControlGroup({
    title: 'Codice',
    layout: ControlGroupLayouts.FLEX_ROW,
    controlledShapeTypes: [ShapeTypes.BARCODE],
    shapeControls: [
        new SimpleTextShapeControl({
            label: 'Contenuto del codice a barre',
            controlType: ControlTypes.SIMPLE_TEXT,
            displayMeasureUnit: false,
            measureUnitOverride: 'Se il codice a barre non viene visualizzato, il codice inserito non è corretto',
            observableValue: editor.getSelectedShape<ObservableBarcode>().code as string,
            setter: (value: string) => editor.getSelectedShape<ObservableBarcode>().setCode(
                value,
                {applyToPamelaShape: true}),
        }, {
            placeholder: 'Inserire il codice',
            rightElementRenderer: linkFieldPopoverRenderer,
        }),
    ]
});

const encodingControlGroup = (editor: Editor) => controlGroupBuilder({
    title: 'Specifica',
    layout: ControlGroupLayouts.FLEX_ROW,
    controlledShapeTypes: [ShapeTypes.BARCODE],
    shapeControls: [
        new SelectShapeControl({
            label: 'Nome della specifica',
            controlType: ControlTypes.SELECT,
            displayMeasureUnit: false,
            observableValue: (editor.getSelectedShape<ObservableBarcode>()).encoding as string,
            setter: (value: string) => editor.getSelectedShape<ObservableBarcode>().setEncoding(
                value,
                {applyToPamelaShape: true})
        }, {items: barcodeEncodings})
    ]
});

const additionalControls = (editor: Editor) => controlGroupBuilder({
    title: 'Controlli aggiuntivi',
    layout: ControlGroupLayouts.FLEX_ROW,
    controlledShapeTypes: [ShapeTypes.BARCODE],
    shapeControls: [
        new BooleanShapeControl({
            label: 'Mostra valore',
            controlType: ControlTypes.CHECKBOX,
            displayMeasureUnit: false,
            measureUnitOverride: 'Mostra o nascondi il codice',
            observableValue: editor.getSelectedShape<ObservableBarcode>().displayValue,
            setter: (value) => editor.getSelectedShape<ObservableBarcode>().setDisplayedValue(
                value,
                {applyToPamelaShape: true})
        })
    ]
});

const simpleTextControls = (editor: Editor) => controlGroupBuilder({
    title: 'Contenuto',
    layout: ControlGroupLayouts.WHOLE_ROW,
    controlledShapeTypes: [ShapeTypes.TEXT],
    shapeControls: [
        new TextAreaShapeControl({
            label: 'Testo',
            controlType: ControlTypes.RICH_TEXT,
            displayMeasureUnit: false,
            measureUnitOverride: 'Contenuto della casella di testo. \nCTRL + Enter per applicare velocemente. Doppie graffe per inserire un valore Built-In',
            observableValue: editor.getSelectedShape<ObservableText>().text,
            setter: (value) => {
                editor.getSelectedShape<ObservableText>().setText(value,
                    {applyToPamelaShape: true}
                );
            }
        }, {placeholder: 'Inserire del testo'}),
    ]
});

const variableNameControls = (editor: Editor) => controlGroupBuilder({
    title: 'Variabile',
    infoCallout: {
        title: '',
        description: 'Puoi utilizzare le variabili esportazione per gestire valori che devono essere preparati dinamicamente per la stampa (E.G. Data di sadenza o numero di lotto)'
    },
    layout: ControlGroupLayouts.WHOLE_ROW,
    controlledShapeTypes: [ShapeTypes.EXPORT_VARIABLE],
    shapeControls: [
        new SimpleTextShapeControl({
            label: 'Nome',
            controlType: ControlTypes.SIMPLE_TEXT,
            measureUnitOverride: 'Identificatore della variabile',
            observableValue: editor.getSelectedShape<ObservableVariable>().variableName,
            setter: (value) => editor.getSelectedShape<ObservableVariable>().setVariableName(
                value,
                {applyToPamelaShape: true})
        }, {placeholder: 'Inserire un nome valido'})
    ]
});


export const DataControls = {
    codeControlGroup,
    encodingControlGroup,
    additionalControls,
    simpleTextControls,
    variableNameControls
};
